import { Claim, ClaimItemValues } from 'types/claim';
import moment from 'moment';
import { Button } from './Button';
import { Modal } from './Modal';
import { useState } from 'react';
import { withNotification } from './Notification';
import { NotificationTypes, apiErrorHandler, formatStatusText } from 'utils';
import { useMutation } from '@apollo/client';
import { CANCEL_RESOLUTION } from 'gql/mutations';
import { CheckCircleIcon } from '@heroicons/react/solid';

type ClaimResolutionActivitiesProps = {
  claim: Claim;
  claimItem: ClaimItemValues;
  option?: string;
};

type Activity = {
  timestamp: string;
  content: string;
  type: 'resolution' | 'rule' | 'option';
  action: {
    text: string;
    callback: () => void;
    customClass?: string;
  };
};

export const ClaimResolutionActivities = withNotification(
  ({
    claim,
    claimItem,
    showNotification,
    option,
  }: ClaimResolutionActivitiesProps & {
    showNotification: (type: any, content: string) => void;
  }) => {
    const settlement = claimItem.settlement
      ? claim?.settlements?.find((s: any) =>
          s.items?.find((si: any) => si?.claimItem?.id === claimItem?.id),
        )
      : null;

    const [confirmationModal, setConfirmatioModal] = useState<boolean>(false);

    const toggleConfirmationModal = () =>
      setConfirmatioModal((isOpen) => !isOpen);

    const [cancelResolution, { loading: cancelResolutionLoading }] =
      useMutation(CANCEL_RESOLUTION, {
        onError(error) {
          const newError = apiErrorHandler(error);
          showNotification(NotificationTypes.error, newError?.message);
        },
        onCompleted(data) {
          showNotification(
            NotificationTypes.success,
            'Resolution is cancelled',
          );
          setConfirmatioModal(false);

          return setTimeout(() => {
            return window.location.reload();
          }, 300);
        },
      });

    const handleCancelResolution = () => {
      console.log(claimItem);
      if (!claimItem) {
        showNotification(NotificationTypes.error, 'Invalid Item Quantity');
        return;
      }

      const settlementItemId = settlement.items.find(
        (si: any) => si?.claimItem?.id === claimItem?.id,
      )?.id;
      let payload = {
        claimId: claim.id,
        storeId: claim.storeId,
        claimItemId: claimItem.id,
        settlementItemId: settlementItemId,
      };

      cancelResolution({
        variables: { input: payload },
      });
    };

    if (!settlement) return null;

    const activities: Activity[] = [
      {
        timestamp: settlement.updated_at,
        type: 'resolution',
        content: `Issue item ${
          claimItem.orderItem.title
        } has been resolved with status ${formatStatusText(
          `${claimItem.resolution}`,
        )}`,
        action: {
          text: 'Cancel Resolution',
          callback: () => {
            setConfirmatioModal(true);
          },
          customClass:
            'text-red-700 bg-red-100 border-red-400 hover:bg-red-200 focus:outline-none focus:ring focus:ring-red-300',
        },
      },
    ];

    // Add rule activity if exists
    if (claimItem.settlement.solvedByRule?.title) {
      activities.push({
        timestamp: settlement.updated_at,
        type: 'rule',
        content: `Solved by Rule: ${claimItem.settlement.solvedByRule.title}`,
        action: {
          text: 'View Rule Details',
          callback: () => console.log('View rule details'),
          customClass:
            'text-blue-700 bg-blue-100 border-blue-400 hover:bg-blue-200',
        },
      });
    }

    // Add option activity if exists
    if (option) {
      activities.push({
        timestamp: settlement.updated_at,
        type: 'option',
        content: `Option: ${formatStatusText(option)}`,
        action: {
          text: 'View Details',
          callback: () => console.log('View option details'),
          customClass:
            'text-green-700 bg-green-100 border-green-400 hover:bg-green-200',
        },
      });
    }

    const getActivityIcon = (type: string) => {
      switch (type) {
        case 'resolution':
          return <CheckCircleIcon className="w-4 h-4 text-blue-600" />;
        case 'rule':
          return <div className="w-4 h-4 bg-purple-600 rounded"></div>;
        case 'option':
          return <CheckCircleIcon className="w-4 h-4 text-green-600" />;
        default:
          return <CheckCircleIcon className="w-4 h-4 text-gray-600" />;
      }
    };

    const getActivityBgColor = (type: string) => {
      switch (type) {
        case 'resolution':
          return 'bg-blue-100';
        case 'rule':
          return 'bg-purple-100';
        case 'option':
          return 'bg-green-100';
        default:
          return 'bg-gray-100';
      }
    };

    return (
      <div className="my-5">
        <div className="border border-gray-300 p-4 rounded-md mt-4">
          {/* Timeline Header */}
          <h3 className="font-semibold text-gray-900 mb-4">Timeline</h3>

          {/* Timeline Activities */}
          <div className="space-y-4">
            {activities.map((activity, idx) => (
              <div key={`activity-${idx}`} className="flex gap-3">
                <div
                  className={`w-8 h-8 ${getActivityBgColor(
                    activity.type,
                  )} rounded-full flex items-center justify-center flex-shrink-0`}
                >
                  {getActivityIcon(activity.type)}
                </div>
                <div className="flex-1">
                  <div className="flex justify-between items-start mb-2">
                    <p className="text-sm text-gray-900">{activity.content}</p>
                    <p className="text-xs text-gray-500 ml-4">
                      {moment(activity.timestamp).format('HH:mm')}
                    </p>
                  </div>
                  <p className="text-xs text-gray-500 mb-2">
                    {moment(activity.timestamp).fromNow()}
                  </p>
                  <Button
                    onClick={activity.action.callback}
                    className={`text-xs px-3 py-1 ${activity.action.customClass}`}
                  >
                    {activity.action.text}
                  </Button>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Confirmation Modal */}
        <Modal
          customStyle={{ width: '400px' }}
          open={confirmationModal}
          openModal={toggleConfirmationModal}
        >
          <div className="confirmation-modal-header font-bold text-lg">
            Confirm
          </div>
          <div className="confirmation-modal-content my-4 text-sm">
            Cancelling this resolution might not reflect in your store and you
            may need to cancel the reshipment or refund created manually in your
            store admin
          </div>
          <div className="confirmation-modal-footer flex justify-between items-center">
            <div
              onClick={toggleConfirmationModal}
              className="text-gray-800 cursor-pointer font-semibold"
            >
              Back
            </div>
            <Button
              onClick={handleCancelResolution}
              disabled={cancelResolutionLoading}
              className="text-red-700 bg-red-100 border-red-400 hover:bg-red-200 focus:outline-none focus:ring focus:ring-red-300"
            >
              Cancel Resolution
            </Button>
          </div>
        </Modal>
      </div>
    );
  },
);
